import React, { useState } from 'react';
import { Key, Laptop, Globe, Layers, Database, Eye, Cog, Shield } from 'lucide-react';
import CisaFunctionCard from './CisaFunctionCard';
import { CISA_PILLARS, CISA_FUNCTIONS, CROSS_CUTTING } from '../config/cisaMaturityModel';

const PILLAR_ICONS = {
  IDENTITY: Key,
  DEVICES: Laptop,
  NETWORKS: Globe,
  APPLICATIONS: Layers,
  DATA: Database,
  VISIBILITY: Eye,
  AUTOMATION: Cog,
  GOVERNANCE: Shield
};

/**
 * CisaFunctionCardList - Container rendering all function cards grouped by pillar
 *
 * Props:
 *   assessments - { [functionCode]: { currentLevel, targetLevel } }
 *   productAdoptions - { [functionCode]: [{ productName, status }] }
 *   onCurrentLevelChange - (functionCode, level) => void
 *   onTargetLevelChange - (functionCode, level) => void
 *   onAdoptProduct - (functionCode, productName, status) => void
 *   onUpdateProductStatus - (functionCode, productName, newStatus) => void
 *   onRemoveProduct - (functionCode, productName) => void
 */
const CisaFunctionCardList = ({
  assessments = {},
  productAdoptions = {},
  onCurrentLevelChange,
  onTargetLevelChange,
  onAdoptProduct,
  onUpdateProductStatus,
  onRemoveProduct
}) => {
  // Track which card is expanded (only one at a time)
  const [expandedCard, setExpandedCard] = useState(null);

  const handleToggleExpand = (functionCode) => {
    setExpandedCard(prev => prev === functionCode ? null : functionCode);
  };

  // Helper to get assessment data for a function
  const getAssessment = (functionCode) => {
    const data = assessments[functionCode];
    // Support both old format (string) and new format (object)
    if (typeof data === 'string') {
      return { currentLevel: data, targetLevel: null };
    }
    return data || { currentLevel: 'TRADITIONAL', targetLevel: null };
  };

  // Helper to get products for a function
  const getProducts = (functionCode) => {
    return productAdoptions[functionCode] || [];
  };

  return (
    <div className="cisa-card-list">
      {/* Render each pillar section */}
      {CISA_PILLARS.map((pillar) => {
        const functions = CISA_FUNCTIONS[pillar.code] || [];
        const Icon = PILLAR_ICONS[pillar.code] || Key;

        return (
          <div key={pillar.code} className="cisa-card-list-pillar">
            {/* Pillar header */}
            <div className="cisa-card-list-pillar-header">
              <span
                className="cisa-card-list-pillar-badge"
                style={{ background: pillar.color }}
              >
                <Icon size={14} />
              </span>
              <span className="cisa-card-list-pillar-name">{pillar.name}</span>
              <span className="cisa-card-list-pillar-count">
                {functions.length} functions
              </span>
            </div>

            {/* Function cards */}
            <div className="cisa-card-list-cards">
              {functions.map((fn) => {
                const assessment = getAssessment(fn.code);
                const products = getProducts(fn.code);

                return (
                  <CisaFunctionCard
                    key={fn.code}
                    functionCode={fn.code}
                    functionName={fn.name}
                    pillarCode={pillar.code}
                    pillarColor={pillar.color}
                    currentLevel={assessment.currentLevel}
                    targetLevel={assessment.targetLevel}
                    adoptedProducts={products}
                    expanded={expandedCard === fn.code}
                    onToggleExpand={() => handleToggleExpand(fn.code)}
                    onCurrentLevelChange={(level) => onCurrentLevelChange?.(fn.code, level)}
                    onTargetLevelChange={(level) => onTargetLevelChange?.(fn.code, level)}
                    onAdoptProduct={(productName, status) => onAdoptProduct?.(fn.code, productName, status)}
                    onUpdateProductStatus={(productName, newStatus) => onUpdateProductStatus?.(fn.code, productName, newStatus)}
                    onRemoveProduct={(productName) => onRemoveProduct?.(fn.code, productName)}
                  />
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Cross-Cutting Capabilities Section */}
      <div className="cisa-card-list-pillar cisa-card-list-pillar--crosscutting">
        <div className="cisa-card-list-pillar-header cisa-card-list-pillar-header--crosscutting">
          <span className="cisa-card-list-crosscutting-badge">
            <Shield size={14} />
          </span>
          <span className="cisa-card-list-pillar-name">Cross-Cutting Capabilities</span>
          <span className="cisa-card-list-pillar-count">
            {CROSS_CUTTING.length} capabilities
          </span>
        </div>

        <div className="cisa-card-list-cards">
          {CROSS_CUTTING.map((cc) => {
            const assessment = getAssessment(cc.code);
            const products = getProducts(cc.code);
            const Icon = PILLAR_ICONS[cc.code] || Shield;

            return (
              <CisaFunctionCard
                key={cc.code}
                functionCode={cc.code}
                functionName={cc.name}
                pillarCode={cc.code}
                pillarColor="var(--color-primary, #5b8def)"
                currentLevel={assessment.currentLevel}
                targetLevel={assessment.targetLevel}
                adoptedProducts={products}
                expanded={expandedCard === cc.code}
                onToggleExpand={() => handleToggleExpand(cc.code)}
                onCurrentLevelChange={(level) => onCurrentLevelChange?.(cc.code, level)}
                onTargetLevelChange={(level) => onTargetLevelChange?.(cc.code, level)}
                onAdoptProduct={(productName, status) => onAdoptProduct?.(cc.code, productName, status)}
                onUpdateProductStatus={(productName, newStatus) => onUpdateProductStatus?.(cc.code, productName, newStatus)}
                onRemoveProduct={(productName) => onRemoveProduct?.(cc.code, productName)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CisaFunctionCardList;
